'use client'

import { BuilderBlocks } from '@builder.io/react'
import { RadioGroup } from '@headlessui/react'
import { useState } from 'react'

import { Button } from '@src/components/Button/Button'
import { FacetSelector } from '@src/components/ProductConfigurator/modules/FacetSelect/FacetSelect'
import { SKUSelectorV2Dropdown } from '@src/components/SKUSelectorV2/SKUSelectorV2Dropdown'
import { useLoadings } from '@src/hooks/useLoadings'
import { useBreakpoints } from '@src/utils/breakpoints.util'

import type { SKUSelectorV2Props } from './SKUSelectorV2Props'
import { SKUSelectorV2Radio } from './SKUSelectorV2Radio'

const getDefaultSku = (props: SKUSelectorV2Props) => {
  const { style, radio, dropdown, pill } = props
  if (style === 'radio') {
    return radio?.options?.[0]?.isDropdown
      ? (radio?.options?.[0]?.dropdownOptions?.[0]?.sku ?? '')
      : (radio?.options?.[0]?.sku ?? '')
  }
  if (style === 'dropdown') {
    return dropdown?.options?.[0]?.sku ?? ''
  }
  return pill?.options?.[0]?.sku ?? ''
}

export function SKUSelectorV2(props: SKUSelectorV2Props) {
  const { style } = props
  const [selectedSku, setSelectedSku] = useState(getDefaultSku(props))
  const isLoading = useLoadings().loadings.isAddingLineItems ?? false
  const breakpoints = useBreakpoints()
  const isMobile = breakpoints.isMobile || breakpoints.isTablet

  const renderRadio = () => {
    const options = (props.radio?.options ?? []).map((option, index) => ({
      sku: option.sku ?? '',
      content: (
        <BuilderBlocks
          blocks={option.content}
          parentElementId={props.builderBlock?.id}
          dataPath={`component.radio[${index}].content`}
        />
      ),
      isDropdown: option.isDropdown,
      dropdownOptions: (option.dropdownOptions ?? []).map(
        (dropdownOption, dropdownOptionIndex) => ({
          sku: dropdownOption.sku ?? '',
          content: (
            <BuilderBlocks
              blocks={dropdownOption.content}
              parentElementId={props.builderBlock?.id}
              dataPath={`component.radio[${index}].dropdownOptions[${dropdownOptionIndex}].content`}
            />
          ),
          contentTop: (
            <BuilderBlocks
              blocks={dropdownOption.contentTop}
              parentElementId={props.builderBlock?.id}
              dataPath={`component.radio[${index}].dropdownOptions[${dropdownOptionIndex}].contentTop`}
            />
          ),
        })
      ),
    }))
    return (
      <RadioGroup
        className="relative flex shrink-0 flex-col gap-2"
        value={selectedSku}
        onChange={(sku) => sku && !isLoading && setSelectedSku(sku)}
      >
        {options.map((option, index) => (
          <SKUSelectorV2Radio
            key={`${index}-${option.sku}`}
            option={option}
            selectedSku={selectedSku}
            isLoading={isLoading}
            previewAllItems={props.previewAllItems}
            onChange={setSelectedSku}
          />
        ))}
      </RadioGroup>
    )
  }

  const renderDropdown = () => {
    const options = (props.dropdown?.options ?? []).map((option, index) => ({
      sku: option.sku ?? '',
      content: (
        <BuilderBlocks
          blocks={option.content}
          parentElementId={props.builderBlock?.id}
          dataPath={`component.dropdown[${index}].content`}
        />
      ),
      contentTop: (
        <BuilderBlocks
          blocks={option.contentTop}
          parentElementId={props.builderBlock?.id}
          dataPath={`component.dropdown[${index}].contentTop`}
        />
      ),
    }))

    const activeOption = options.find((option) => option.sku === selectedSku)
    return (
      <SKUSelectorV2Dropdown
        dropdownOptions={options}
        activeOption={activeOption}
        selectedSku={selectedSku}
        isLoading={isLoading}
        onChange={setSelectedSku}
        previewAllItems={props.previewAllItems}
      />
    )
  }

  const renderPill = () => {
    return (
      <FacetSelector
        facetKey="sku"
        facetOptions={(props.pill?.options ?? []).map((option, index) => ({
          slug: option.sku ?? '',
          displayName: null,
          content: (
            <BuilderBlocks
              blocks={option.content}
              parentElementId={props.builderBlock?.id}
              dataPath={`component.pill[${index}].content`}
            />
          ),
          isAvailable: true,
          isSelected: option.sku === selectedSku,
          priority: 0,
          imagePrimary: null,
          imageSecondary: null,
        }))}
        onFacetSelected={setSelectedSku}
      />
    )
  }

  return (
    <div data-testid="sku-selector">
      {style === 'radio' && renderRadio()}
      {style === 'dropdown' && renderDropdown()}
      {style === 'pill' && renderPill()}

      <Button
        className="mt-4 w-full"
        isLoading={isLoading}
        variant={props.addToCartVariant ?? 'primary'}
        size={isMobile ? 'medium' : 'large'}
        actionName="addItemsToCart"
        addItemsToCart={{
          skus: [{ sku: selectedSku, quantity: 1 }],
          redirectUrl: props.redirectUrl,
        }}
      >
        <BuilderBlocks
          blocks={props.addToCartContent}
          parentElementId={props.builderBlock?.id}
          dataPath={`component.addToCartContent`}
        />
      </Button>
    </div>
  )
}
